import { useState } from 'react';
import {
  Container,
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  useColorModeValue,
  Divider,
  List,
  ListItem,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import { FiDownload } from 'react-icons/fi';
import { Icon } from '@chakra-ui/react';

/**
 * Policy Viewer Page - Displays AI User Acknowledgement Policy
 * Migrated from Supabase/Tailwind to Firestore/Chakra UI
 */
export function PolicyViewerPage() {
  const [isPrinting, setIsPrinting] = useState(false);

  const bgColor = useColorModeValue('white', 'gray.800');
  const headingColor = useColorModeValue('gray.900', 'white');
  const subheadingColor = useColorModeValue('gray.600', 'gray.400');
  const textColor = useColorModeValue('gray.800', 'gray.200');

  const handlePrint = () => {
    setIsPrinting(true);
    window.print();
    setTimeout(() => setIsPrinting(false), 1000);
  };

  return (
    <>
      <Container maxW="container.xl" py={8}>
        <Box
          className="no-print"
          mb={6}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          borderBottomWidth="1px"
          pb={4}
        >
          <Heading size="lg" color={headingColor}>
            AI User Acknowledgement Policy
          </Heading>
          <Button
            leftIcon={<Icon as={FiDownload} />}
            colorScheme="blue"
            onClick={handlePrint}
            isLoading={isPrinting}
            loadingText="Preparing..."
          >
            Save as PDF
          </Button>
        </Box>

        <Box className="policy-content" color={textColor}>
          <VStack spacing={6} align="stretch">
            {/* Header */}
            <Box textAlign="center" mb={8}>
              <Heading size="2xl" color={headingColor} mb={2}>
                Future Home Loans
              </Heading>
              <Heading size="xl" color={subheadingColor} mb={2}>
                AI-Powered Rock Evaluation
              </Heading>
              <Heading size="lg" color={subheadingColor} mb={4}>
                End User Acknowledgement Policy
              </Heading>
              <Text fontSize="sm" color={subheadingColor}>
                Version 1.0 - November 2025
              </Text>
            </Box>

            {/* Section 1 */}
            <Box>
              <Heading size="lg" color={headingColor} borderBottomWidth="2px" pb={2} mb={4}>
                1. Purpose and Scope
              </Heading>
              <Text lineHeight="relaxed">
                This policy governs the use of AI-powered Rock evaluation features within the Future Home Loans
                (FHL) Rocks management system. By using the AI evaluation feature, you acknowledge and agree
                to the terms outlined in this document.
              </Text>
            </Box>

            {/* Section 2 */}
            <Box>
              <Heading size="lg" color={headingColor} borderBottomWidth="2px" pb={2} mb={4}>
                2. AI Technology Used
              </Heading>
              <Text lineHeight="relaxed">
                The AI Rock evaluation feature utilizes OpenAI's GPT-4 language model to analyze and provide
                feedback on your Rocks (quarterly goals). The AI evaluates your Rocks based on SMART criteria
                (Specific, Measurable, Achievable, Relevant, Time-bound) and provides suggestions for improvement.
              </Text>
            </Box>

            {/* Section 3 */}
            <Box>
              <Heading size="lg" color={headingColor} borderBottomWidth="2px" pb={2} mb={4}>
                3. Data Processing and Privacy
              </Heading>
              <VStack spacing={3} align="stretch">
                <Box>
                  <Heading size="md" color={headingColor} mb={2}>
                    3.1 Information Sent to AI
                  </Heading>
                  <Text lineHeight="relaxed" mb={2}>
                    When you use the AI evaluation feature, the following information is sent to OpenAI:
                  </Text>
                  <List spacing={2} styleType="disc" pl={6}>
                    <ListItem>Your Rock description and goal statement</ListItem>
                    <ListItem>Associated milestones and progress indicators</ListItem>
                    <ListItem>Timeframe and completion criteria</ListItem>
                  </List>
                </Box>

                <Box>
                  <Heading size="md" color={headingColor} mb={2} mt={4}>
                    3.2 Data Protection
                  </Heading>
                  <List spacing={2} styleType="disc" pl={6}>
                    <ListItem>No personally identifiable information (PII) is intentionally sent to the AI service</ListItem>
                    <ListItem>OpenAI's data processing complies with their published privacy policy and data usage terms</ListItem>
                    <ListItem>AI-generated responses are stored in our secure database for your reference</ListItem>
                    <ListItem>You retain ownership of all data you input into the system</ListItem>
                  </List>
                </Box>
              </VStack>
            </Box>

            {/* Section 4 */}
            <Box>
              <Heading size="lg" color={headingColor} borderBottomWidth="2px" pb={2} mb={4}>
                4. AI Limitations and Responsibilities
              </Heading>
              <VStack spacing={3} align="stretch">
                <Box>
                  <Heading size="md" color={headingColor} mb={2}>
                    4.1 AI as a Tool
                  </Heading>
                  <Text lineHeight="relaxed">
                    The AI evaluation feature is designed to assist and provide suggestions. It should be used
                    as a tool to enhance your goal-setting process, not as the sole determinant of your goals'
                    quality or viability.
                  </Text>
                </Box>

                <Box>
                  <Heading size="md" color={headingColor} mb={2} mt={4}>
                    4.2 Human Judgment Required
                  </Heading>
                  <List spacing={2} styleType="disc" pl={6}>
                    <ListItem>AI suggestions are advisory only and require human review and judgment</ListItem>
                    <ListItem>You are responsible for the final content and quality of your Rocks</ListItem>
                    <ListItem>AI feedback may not account for all context-specific factors relevant to your goals</ListItem>
                    <ListItem>The AI may occasionally provide inaccurate or incomplete recommendations</ListItem>
                  </List>
                </Box>

                <Box>
                  <Heading size="md" color={headingColor} mb={2} mt={4}>
                    4.3 No Guarantee of Success
                  </Heading>
                  <Text lineHeight="relaxed">
                    Using the AI evaluation feature does not guarantee the success or achievement of your Rocks.
                    Goal achievement depends on many factors including planning, execution, resources, and external
                    circumstances.
                  </Text>
                </Box>
              </VStack>
            </Box>

            {/* Section 5 */}
            <Box>
              <Heading size="lg" color={headingColor} borderBottomWidth="2px" pb={2} mb={4}>
                5. Appropriate Use
              </Heading>
              <VStack spacing={3} align="stretch">
                <Box>
                  <Heading size="md" color={headingColor} mb={2}>
                    5.1 Permitted Uses
                  </Heading>
                  <List spacing={2} styleType="disc" pl={6}>
                    <ListItem>Evaluating and improving your quarterly Rocks (goals)</ListItem>
                    <ListItem>Getting suggestions for making goals more SMART and achievable</ListItem>
                    <ListItem>Identifying potential issues with milestone planning</ListItem>
                    <ListItem>Brainstorming ways to refine goal statements</ListItem>
                  </List>
                </Box>

                <Box>
                  <Heading size="md" color={headingColor} mb={2} mt={4}>
                    5.2 Prohibited Uses
                  </Heading>
                  <List spacing={2} styleType="disc" pl={6}>
                    <ListItem>Submitting confidential, proprietary, or sensitive business information</ListItem>
                    <ListItem>Including personal information about customers, employees, or third parties</ListItem>
                    <ListItem>Using the service to evaluate goals that violate company policies or legal requirements</ListItem>
                    <ListItem>Attempting to manipulate or exploit the AI system</ListItem>
                  </List>
                </Box>
              </VStack>
            </Box>

            {/* Section 6 */}
            <Box>
              <Heading size="lg" color={headingColor} borderBottomWidth="2px" pb={2} mb={4}>
                6. Security and Confidentiality
              </Heading>
              <Text lineHeight="relaxed">
                While we implement security measures to protect your data, you should avoid including highly
                sensitive or confidential information in your Rock descriptions when using the AI evaluation
                feature. Always review content before submitting it for AI analysis.
              </Text>
            </Box>

            {/* Section 7 */}
            <Box>
              <Heading size="lg" color={headingColor} borderBottomWidth="2px" pb={2} mb={4}>
                7. Rate Limiting and Usage
              </Heading>
              <Text lineHeight="relaxed">
                AI evaluation features are subject to rate limiting to ensure fair usage and manage costs.
                Excessive or automated use may be restricted. Current limits are displayed in the application
                and may be adjusted based on usage patterns and service availability.
              </Text>
            </Box>

            {/* Section 8 */}
            <Box>
              <Heading size="lg" color={headingColor} borderBottomWidth="2px" pb={2} mb={4}>
                8. Changes to This Policy
              </Heading>
              <Text lineHeight="relaxed">
                Future Home Loans reserves the right to update this policy as needed. Users will be notified
                of significant changes and may be required to review and accept updated terms before continuing
                to use AI features.
              </Text>
            </Box>

            {/* Section 9 */}
            <Box>
              <Heading size="lg" color={headingColor} borderBottomWidth="2px" pb={2} mb={4}>
                9. Contact and Support
              </Heading>
              <Text lineHeight="relaxed">
                If you have questions about this policy or the AI evaluation feature, please contact your
                system administrator or Future Home Loans IT support.
              </Text>
            </Box>

            {/* Section 10 */}
            <Box>
              <Heading size="lg" color={headingColor} borderBottomWidth="2px" pb={2} mb={4}>
                10. Acknowledgement
              </Heading>
              <Text lineHeight="relaxed" mb={4}>
                By accepting this policy, you acknowledge that you have read, understood, and agree to these
                terms. You understand that:
              </Text>
              <List spacing={2} styleType="disc" pl={6}>
                <ListItem>AI-generated feedback is advisory and requires human judgment</ListItem>
                <ListItem>Your Rock data will be processed by third-party AI services</ListItem>
                <ListItem>You are responsible for the appropriateness and accuracy of content you submit</ListItem>
                <ListItem>You should not include sensitive or confidential information in AI evaluations</ListItem>
                <ListItem>The AI feature is subject to availability and usage limits</ListItem>
              </List>
            </Box>

            {/* Footer */}
            <Divider mt={8} />
            <Box textAlign="center" pt={6}>
              <Text fontWeight="semibold" mb={2} color={headingColor}>
                Future Home Loans - FHL Rocks System
              </Text>
              <Text color={subheadingColor}>AI User Acknowledgement Policy - Version 1.0</Text>
              <Text color={subheadingColor} mt={2}>
                © 2025 Future Home Loans. All rights reserved.
              </Text>
            </Box>
          </VStack>
        </Box>
      </Container>

      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background: white;
          }
          .policy-content {
            font-size: 12pt;
            line-height: 1.6;
          }
        }
      `}</style>
    </>
  );
}

